import { BlurView } from 'expo-blur'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Divider } from 'react-native-paper'

import { useFocusEffect, useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { TaskStatus, TournamentAccessControl } from '../../models/api-store'
import { DailyTaskType } from '../../models/session/session-daily'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { SkeletonLoader } from './SkeletonLoader'
import { TaskList } from './TaskList'
import VideoModal from './VideoModal'

const logger = extendLogger(LoggerContexts.DailyTask)

export enum DailyTaskList {
  userFirstVisit = 'userFirstVisit',
  returningUser = 'returningUser',
  preEvent = 'preEvent',
  postEvent = 'postEvent'
}

export const DailyTask = observer(({ taskListType }: { taskListType: DailyTaskList }) => {
  const router = useRouter()
  const {
    environment: { isProd },
    analytics: { setEvent },
    session: {
      dailyTasklist,
      getDailyTask,
      taskDay,
      incrementTaskDay,
      resetDaily,
      setSelectedDailyTask,
      updateDailyTaskStatus,
      user,
      isTodaysTaskCompleted,
      isTodaysFirstTask
    }
  } = useStore()
  const userId = user?._id.toString()
  const [isFetchingTaskList, setIsFetchingTaskList] = useState(true)
  const [videoPlayerState, setVideoPlayerState] = useState({ videoUrl: '', isVisible: false })

  const fetchUserDailyTaskList = useCallback(async () => {
    try {
      setIsFetchingTaskList(true)
      await getDailyTask()
    } catch (error) {
      logger.error('fetchUserDailyTaskList error', error)
    } finally {
      setIsFetchingTaskList(false)
    }
  }, [getDailyTask])

  useEffect(() => {
    logger.info('fetchUserDailyTaskList taskDay', taskDay)
    fetchUserDailyTaskList()
  }, [fetchUserDailyTaskList, taskDay])

  useFocusEffect(
    useCallback(() => {
      fetchUserDailyTaskList()
    }, [fetchUserDailyTaskList])
  )

  const onTaskSelected = (task: DailyTaskType) => {
    if (isTodaysFirstTask) {
      setEvent(events.dailyTask.daily_tasks_started, {
        userId,
        day: task.day,
        week: task.week
      })
    }
    setSelectedDailyTask({
      taskId: task.id,
      taskName: task.daily_task,
      status: task.status,
      day: task.day,
      week: task.week,
      category: task.daily_task_category
    })
    setEvent(events.dailyTask.task_started, {
      userId,
      category: task.daily_task_category,
      day: task.day,
      week: task.week,
      task_name: task.daily_task
    })
    const route = task.daily_task_deeplink_url.split('/')

    logger.info('onTaskSelected', route)

    switch (route[2]) {
      case 'modules': {
        if (route[3]) {
          const moduleId = Number(route[3])
          router.navigate(`/lobby/zogo?moduleId=${moduleId}`)
        }
        break
      }
      case 'pokerish':
        // TODO: REVIEW: should this go to game-over?
        // navigation.navigate(DrawerRoutes.game, { screen: GameRoutes.threeCardGameOverScreen })
        router.navigate('/game/three-card/game-over')
        break
      case 'practice-game':
        router.navigate('/game/new')
        break
      case 'poker-101':
        logger.info('Poker 101 video', task.id)
        setVideoPlayerState({
          videoUrl: task.daily_task_video_url,
          isVisible: true
        })
        break
      case 'learn-tab': {
        updateDailyTaskStatus()
        router.replace('/(root)/(tabs)/learning')
        break
      }
      case 'play-tab': {
        updateDailyTaskStatus()
        router.replace('/(root)/(tabs)/play')
        break
      }
      case 'table-tour':
        setEvent(events.homescreen.table_tour, {
          userId: user?._id.toString()
        })
        router.navigate('/game/table-tour')
        break
      case 'my-first-game':
        setEvent('First Game Started', { from_source: 'Daily task' })
        router.navigate('/game/my-first-game')
        break
      case 'raise-or-fold':
        setEvent(events.homescreen.raise_or_fold_tapped, {
          userId: user?._id.toString()
        })
        router.navigate('/game/raise-or-fold')
        break
      case 'deal-the-flop':
        setEvent(events.homescreen.deal_the_flop_started, {
          userId: user?._id.toString()
        })
        router.navigate('/game/deal-the-flop')
        break
      case 'hand-rankings':
      case 'create-game':
      case 'quick-game':
      case 'community-game':
        router.navigate('/game/create/new')

        // setTimeout(async () => {
        //   await Linking.openURL('https://drive.google.com/file/d/19l0CvWZKSUJEsjFBx4VezMSg-0X7B_SR/view?pli=1')
        // }, 500)
        break
      case 'profile-tab':
        // TODO: REVIEW: should this go to /profile ?
        router.navigate('/onboarding/name')
        break
      case 'Schedule a game for later':
        router.navigate(`/game/create/tournament?accessControl=${TournamentAccessControl.private}`)
        break
    }
  }

  return (
    <React.Fragment key={'daily-task'}>
      {dailyTasklist.length !== 0 && (
        <BlurView intensity={80} tint="light" style={styles.frameContainer}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <DS.Text style={styles.containerPadding} variant="Heading 3/Medium">
              Daily tasks
            </DS.Text>
            {isProd() ? null : (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', columnGap: 10 }}>
                <TouchableOpacity disabled={dailyTasklist.length === 0} onPress={incrementTaskDay}>
                  <DS.Text style={styles.containerPadding} variant="Body Small/Medium">
                    Next
                  </DS.Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={resetDaily}>
                  <DS.Text style={styles.containerPadding} variant="Body Small/Medium">
                    Reset
                  </DS.Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
          <View style={styles.taskCompletedContainer}>
            {isTodaysTaskCompleted && (
              <>
                <Image source={require('./assets/Stars.png')} resizeMode="contain" style={styles.starsImage} />
                <DS.Text textAlign="center" variant="Heading 4/Medium" style={{ marginVertical: 10 }}>
                  Excellent!
                </DS.Text>
                <DS.Text textAlign="center" variant="Body Medium/Regular">
                  All daily tasks are complete.
                </DS.Text>
              </>
            )}
          </View>
          {isFetchingTaskList ? (
            <SkeletonLoader height={300} />
          ) : (
            dailyTasklist.length > 0 && (
              <View style={styles.listContainer}>
                {dailyTasklist.map((task, index) => {
                  const title = task.daily_task
                  const subTitle = task.daily_task_subheader
                  const icon = task.daily_task_icon
                  return (
                    <Fragment key={`${taskListType}-${title}-${index}-parent`}>
                      <TaskList
                        key={`${task.id}`}
                        title={title}
                        subTitle={subTitle}
                        leftIcon={icon}
                        isTaskCompleted={task.status === TaskStatus.complete}
                        onTaskSelected={() => {
                          if (task.status === TaskStatus?.incomplete) {
                            onTaskSelected(task)
                          }
                        }}
                      />
                      {index !== dailyTasklist.length - 1 && <Divider style={styles.taskSeperator} />}
                    </Fragment>
                  )
                })}
              </View>
            )
          )}
        </BlurView>
      )}
      <DS.Frame style={styles.videoModal}>
        <VideoModal
          url={videoPlayerState.videoUrl}
          isVideoPlayerVisible={videoPlayerState.isVisible}
          closeVideoPlayer={() => setVideoPlayerState({ ...videoPlayerState, isVisible: false })}
          videoProps={{ contentFit: 'fill', style: { width: '100%', height: '100%' } }}
        />
      </DS.Frame>
    </React.Fragment>
  )
})

const styles = StyleSheet.create({
  frameContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.50)',
    borderRadius: 12,
    padding: DS.spacing[16],
    marginTop: 10,
    overflow: 'hidden'
  },
  listContainer: {
    padding: DS.spacing[16],
    backgroundColor: 'white',
    borderRadius: 8,
    marginTop: 10,
    shadowColor: 'rgba(0, 0, 0, 0.06)',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5
  },
  containerPadding: {
    // paddingHorizontal: DS.spacing[16]
  },
  taskCompletedContainer: {
    marginVertical: 10,
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center'
  },
  starsImage: {
    width: 24,
    height: 24
  },
  taskSeperator: { marginVertical: 15, width: '80%', alignSelf: 'flex-end' },
  videoModal: {
    marginHorizontal: 24
  }
})
